import Avatar from '@components/avatar'
import classnames from 'classnames'
import {
    Box,
    DollarSign, Search,
    TrendingUp,
    User
} from 'react-feather'
import {
    Card,
    CardHeader,
    CardTitle,
    CardBody,
    Spinner,
    Col,
    Media,
    Row,
    UncontrolledTooltip, FormGroup, Button, Form
} from 'reactstrap'
import {useState, useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {getDashboardStatsValues} from '@store/actions/adminDashboard'
import {PropTypes} from "prop-types"
import {useFormik} from "formik";
import moment from "moment/moment";
import Flatpickr from "react-flatpickr";

const StatsCard = ({cols}) => {
    const [isStatsLoading, setIsStatsLoading] = useState(false)
    const [isDispatched, setIsDispatched] = useState(true)
    const [statsData, setStatsData] = useState(null)

    const filterForm = useFormik({
        initialValues: {
            startDate: moment().startOf('month').toDate(),
            endDate: moment().endOf('month').toDate()
        },
        validate: values => {
            const errors = {}
            if (values.endDate < values.startDate) {
                errors.endDate = 'End date must be greater than or equal to start date'
            }
            return errors
        },
        onSubmit: values => {
            if (values.endDate >= values.startDate) {
                const startDate = moment(values.startDate).format('YYYY-MM-DD')
                const endDate = moment(values.endDate).format('YYYY-MM-DD')
                dispatch(getDashboardStatsValues(`?start_date=${startDate}&end_date=${endDate}`))
            }
        }
    })

    const dispatch = useDispatch()
    const adminDashboardStoreSlice = useSelector(state => state.adminDashboardSlice)

    useEffect(() => {
        if (!!adminDashboardStoreSlice?.stats) {
            setStatsData(adminDashboardStoreSlice?.stats?.data)
            setIsStatsLoading(false)
        }
    }, [adminDashboardStoreSlice])

    useEffect(() => {
        if (isDispatched) {
            setIsStatsLoading(true)
            const startDate = moment(filterForm.values.startDate).format('YYYY-MM-DD')
            const endDate = moment(filterForm.values.endDate).format('YYYY-MM-DD')
            dispatch(getDashboardStatsValues(`?start_date=${startDate}&end_date=${endDate}`))
            setIsDispatched(false)
        }
    }, [])

    const statsList = [
        {
            title: statsData?.all_brands,
            id: 970,
            subtitle: 'All Brands',
            subtitleHover: 'paid vs. free',
            color: 'light-primary',
            icon: <TrendingUp size={21}/>
        },
        {
            title: statsData?.all_rds,
            id: 971,
            subtitle: "All RDs",
            color: 'light-info',
            icon: <User size={21}/>
        },
        {
            title: statsData?.open_brand_campaigns,
            id: 972,
            subtitle: "Open Brand Outreaches",
            color: 'light-info',
            icon: <User size={21}/>
        },
        {
            title: statsData?.available_rd_opportunities,
            id: 973,
            subtitle: "Available Partnership Pitches",
            color: 'light-info',
            icon: <User size={21}/>
        },
        {
            title: statsData?.partnerships_secured,
            id: 974,
            subtitle: 'Partnerships Secured',
            subtitleHover: 'total vs monthly vs quarterly',
            color: 'light-primary',
            icon: <TrendingUp size={21}/>
        },
        {
            title: statsData?.total_logins,
            id: 975,
            subtitle: 'Unique/Total Logins',
            subtitleHover: 'unique total logins',
            color: 'light-primary',
            icon: <TrendingUp size={21}/>
        },
        {
            title: statsData?.brand_logins,
            id: 978,
            subtitle: 'Brand Unique/Total Logins',
            subtitleHover: 'unique/total brand logins',
            color: 'light-primary',
            icon: <TrendingUp size={21}/>
        },
        {
            title: statsData?.rd_logins,
            id: 979,
            subtitle: 'RD Unique/Total Logins',
            subtitleHover: 'unique/total RD logins',
            color: 'light-primary',
            icon: <TrendingUp size={21}/>
        },
        {
            title: statsData?.total_revenue,
            id: 976,
            subtitle: 'Total Revenue',
            subtitleHover: `Subscription (${statsData?.subscription}) - Commission (${statsData?.commission})`,
            color: 'light-success',
            icon: <DollarSign size={21}/>
        },
        {
            title: statsData?.churn,
            id: 977,
            subtitle: 'Brands Churned',
            color: 'light-danger',
            icon: <Box size={21}/>
        }
    ]

    const renderData = () => {
        return statsList.map((item, index) => {
            const margin = Object.keys(cols)
            return (
                <Col
                    key={index}
                    {...cols}
                    className={classnames({
                        [`mb-2`]: index !== statsList.length - 1
                    })}
                >
                    <Media>
                        <Avatar id={`av-tooltip-${item.id}`} color={item.color} icon={item.icon} className='mr-1'/>
                        {item.subtitleHover && <UncontrolledTooltip placement='top' target={`av-tooltip-${item.id}`}>{item.subtitleHover}</UncontrolledTooltip>}
                        <Media className='media-avatar m-auto' body>
                            <h4 className='font-weight-bolder mb-0'>{item.title}</h4>
                            <h6 className='total-partnership'>{item.subtitle}</h6>
                        </Media>
                    </Media>
                </Col>
            )
        })
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle>Statistics</CardTitle>
                <Form onSubmit={filterForm.handleSubmit}>
                    <Row>
                        <Col md={4}>
                            <FormGroup>
                                <Flatpickr className='form-control flatpickr-input'
                                           id='start-date'
                                           placeholder='Start Date'
                                           options={{dateFormat: 'd M, Y'}}
                                           value={filterForm.values.startDate}
                                           onChange={(date) => {
                                               filterForm.setFieldValue('startDate', date[0])
                                               // Validate end date when start date changes
                                               if (filterForm.values.endDate < date[0]) {
                                                   filterForm.setFieldError('endDate', 'End date must be greater than or equal to start date')
                                               } else {
                                                   filterForm.setFieldError('endDate', undefined)
                                               }
                                           }}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <Flatpickr className={`form-control flatpickr-input ${filterForm.errors.endDate ? 'is-invalid' : ''}`}
                                           id='end-date'
                                           placeholder='End Date'
                                           options={{dateFormat: 'd M, Y'}}
                                           value={filterForm.values.endDate}
                                           onChange={(date) => {
                                               if (date[0] >= filterForm.values.startDate) {
                                                   filterForm.setFieldValue('endDate', date[0])
                                                   filterForm.setFieldError('endDate', undefined)
                                               } else {
                                                   filterForm.setFieldValue('endDate', date[0])
                                                   filterForm.setFieldError('endDate', 'End date must be greater than or equal to start date')
                                               }
                                           }}
                                />
                                {filterForm.errors.endDate && (
                                    <div className="invalid-feedback d-block">
                                        {filterForm.errors.endDate}
                                    </div>
                                )}
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <Button.Ripple 
                                color='primary' 
                                className='btn-block' 
                                type='submit'
                                disabled={!!filterForm.errors.endDate}>
                                <Search size={16}/> Find
                            </Button.Ripple>
                        </Col>
                    </Row>
                </Form>
            </CardHeader>
            <CardBody>
                {isStatsLoading && (<div className="w-100 my-4 text-center"><Spinner color='primary'/></div>)}
                {!isStatsLoading && (<Row>{renderData()}</Row>)}
            </CardBody>
        </Card>
    )
}


StatsCard.propTypes = {
    cols: PropTypes.string
}

export default StatsCard
